import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Crown, User, Mail, Calendar, Settings, Zap } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export const AccountPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleManageBilling = async () => {
    try {
      const response = await api.post('/stripe/create-portal-session');
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Portal error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to open billing portal. Please try again.';
      toast.error(errorMessage);
    }
  };

  const getPlanDisplay = () => {
    if (!user?.is_pro) {
      return { label: 'Free', color: 'text-ss-muted', bg: 'bg-ss-muted/20' };
    }
    if (user.pro_type === 'lifetime') {
      return { label: 'Pro Lifetime', color: 'text-ss-gold', bg: 'bg-ss-gold/20' };
    }
    if (user.pro_type === 'monthly') {
      return { label: 'Pro Monthly', color: 'text-ss-cyan', bg: 'bg-ss-cyan/20' };
    }
    return { label: 'Pro Annual', color: 'text-ss-cyan', bg: 'bg-ss-cyan/20' };
  };

  // Show manage billing for monthly and annual subscribers, not for lifetime
  const canManageBilling = user?.is_pro && user?.pro_type !== 'lifetime';

  const plan = getPlanDisplay();

  return (
    <div className="min-h-screen bg-ss-bg">
      <div className="noise-overlay"></div>

      {/* Header */}
      <header className="sticky top-0 z-50 glass-panel">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              data-testid="back-btn"
              className="text-ss-muted hover:text-ss-text"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-xl font-bold tracking-tight uppercase text-ss-text">
                Account
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        {/* Profile Card */}
        <Card className="bg-ss-surface border-ss-border">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-ss-text flex items-center gap-2">
              <User className="w-5 h-5 text-ss-cyan" />
              Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              {user?.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name}
                  className="w-16 h-16 rounded-full border-2 border-ss-border"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-ss-surface-highlight border-2 border-ss-border flex items-center justify-center">
                  <User className="w-8 h-8 text-ss-muted" />
                </div>
              )}
              <div>
                <h2 className="text-xl font-semibold text-ss-text">{user?.name}</h2>
                <p className="text-ss-muted flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {user?.email}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Card */}
        <Card className="bg-ss-surface border-ss-border">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-ss-text flex items-center gap-2">
              <Crown className="w-5 h-5 text-ss-gold" />
              Subscription
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Plan Row - Clean and centered */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-ss-bg rounded-lg border border-ss-border">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full ${plan.bg} flex items-center justify-center`}>
                  <Crown className={`w-5 h-5 ${plan.color}`} />
                </div>
                <div>
                  <span className="text-sm text-ss-muted">Current Plan</span>
                  <p className={`font-heading font-bold text-lg ${plan.color}`}>
                    {plan.label}
                  </p>
                </div>
              </div>
              
              {/* Manage Billing Button - Prominent CTA */}
              {canManageBilling && (
                <Button
                  onClick={handleManageBilling}
                  data-testid="manage-billing-btn"
                  className="bg-ss-cyan hover:bg-ss-cyan/90 text-black font-semibold w-full sm:w-auto"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Manage Billing
                </Button>
              )}
            </div>

            {/* Billing Details */}
            {user?.is_pro && user?.pro_type !== 'lifetime' && user?.pro_current_period_end && (
              <div className="flex items-center justify-between py-2 border-b border-ss-border/50">
                <span className="text-ss-muted flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {user.pro_type === 'monthly' ? 'Next Billing' : 'Renewal Date'}
                </span>
                <span className="text-ss-text font-medium">
                  {new Date(user.pro_current_period_end).toLocaleDateString()}
                </span>
              </div>
            )}

            {user?.pro_status && (
              <div className="flex items-center justify-between py-2">
                <span className="text-ss-muted">Status</span>
                <span className={`px-2.5 py-1 rounded-full text-sm font-medium ${
                  user.pro_status === 'active' 
                    ? 'bg-ss-cyan/20 text-ss-cyan' 
                    : 'bg-ss-muted/20 text-ss-muted'
                }`}>
                  {user.pro_status === 'active' ? 'Active' : user.pro_status}
                </span>
              </div>
            )}

            {/* Lifetime badge */}
            {user?.is_pro && user?.pro_type === 'lifetime' && (
              <div className="flex flex-col items-center gap-2 py-3 bg-ss-gold/10 rounded-lg border border-ss-gold/20">
                <div className="flex items-center gap-2">
                  <Crown className="w-5 h-5 text-ss-gold" />
                  <span className="text-ss-gold font-semibold">Founders Lifetime Access</span>
                </div>
                <span className="text-xs text-ss-muted">No billing to manage</span>
              </div>
            )}

            {/* Change Plan link for Pro subscribers */}
            {user?.is_pro && user?.pro_type !== 'lifetime' && (
              <div className="pt-2 text-center">
                <Button
                  onClick={() => navigate('/upgrade')}
                  variant="ghost"
                  className="text-ss-muted hover:text-ss-cyan text-sm"
                >
                  Change Plan or Upgrade to Lifetime →
                </Button>
              </div>
            )}

            {/* Upgrade CTA for free users */}
            {!user?.is_pro && (
              <div className="pt-2">
                <Button
                  onClick={() => navigate('/upgrade')}
                  data-testid="upgrade-btn"
                  className="w-full bg-ss-gold hover:bg-ss-gold/90 text-black font-semibold h-12"
                >
                  <Zap className="w-5 h-5 mr-2" />
                  Upgrade to Pro
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions Card */}
        <Card className="bg-ss-surface border-ss-border">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-ss-text">
              Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button
              onClick={logout}
              data-testid="logout-btn"
              variant="outline"
              className="w-full border-ss-red/50 hover:border-ss-red text-ss-red hover:bg-ss-red/10"
            >
              Sign Out
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AccountPage;
